import { isHorizontal } from '@Components/D3Tree/utils';
import { ORIENTATION_LEFT_TO_RIGHT, ORIENTATION_TOP_TO_BOTTOM } from '@Components/D3Tree/constants';
import PropTypes from 'prop-types';
import { ORIENTATIONS } from '@Pages/RetroSynthesis/ProjectRoute/ProjectRouteTreeView/constants';
import React, { useEffect } from 'react';
import cn from 'classnames';
import Tooltip, { TooltipConstants } from '@Components/Tooltip';
import { LEAF_ADORNMENT_HEIGHT, LEAF_ADORNMENT_WIDTH, PLUS_SIZE, PLUS_STEM_HEIGHT, PLUS_STEM_WIDTH } from './constants';
import SvgUseAsButton from '../SvgUseAsButton';

import './SubstanceLeafNodeAdornment.scss';

const getAdornmentBounds = (nodeX, nodeY, nodeWidth, nodeHeight, orientation) => {
  if (isHorizontal(orientation)) {
    return {
      x: orientation === ORIENTATION_LEFT_TO_RIGHT ? nodeX + nodeWidth : nodeX - LEAF_ADORNMENT_WIDTH,
      y: nodeY + (nodeHeight - LEAF_ADORNMENT_HEIGHT) / 2,
      width: LEAF_ADORNMENT_WIDTH,
      height: LEAF_ADORNMENT_HEIGHT,
    };
  }
  return {
    x: nodeX + (nodeWidth - LEAF_ADORNMENT_HEIGHT) / 2,
    y: orientation === ORIENTATION_TOP_TO_BOTTOM ? nodeY + nodeHeight : nodeY - LEAF_ADORNMENT_WIDTH,
    width: LEAF_ADORNMENT_HEIGHT,
    height: LEAF_ADORNMENT_WIDTH,
  };
};

const getPlusIconPosition = (orientation) => {
  if (isHorizontal(orientation)) {
    return {
      x: orientation === ORIENTATION_LEFT_TO_RIGHT ? LEAF_ADORNMENT_WIDTH - PLUS_SIZE : -19,
      y: -10,
    };
  }
  return {
    x: 0,
    y: orientation === ORIENTATION_TOP_TO_BOTTOM ? LEAF_ADORNMENT_WIDTH - PLUS_SIZE : 0,
  };
};

const getStemEnds = (orientation) => {
  if (isHorizontal(orientation)) {
    const x1 = orientation === ORIENTATION_LEFT_TO_RIGHT ? 0 : LEAF_ADORNMENT_WIDTH - PLUS_STEM_WIDTH;
    const x2 = x1 + PLUS_STEM_WIDTH;
    const y = LEAF_ADORNMENT_HEIGHT / 2;
    return {
      x1,
      x2,
      y1: y,
      y2: y,
    };
  }
  const y1 = orientation === ORIENTATION_TOP_TO_BOTTOM ? 0 : LEAF_ADORNMENT_WIDTH - PLUS_STEM_WIDTH;
  const y2 = y1 + PLUS_STEM_WIDTH;
  const x = LEAF_ADORNMENT_HEIGHT / 2;
  return {
    x1: x,
    x2: x,
    y1,
    y2,
  };
};

function SubstanceLeafNodeAdornment({ nodeX, nodeY, nodeWidth, nodeHeight, orientation, onClick, disabled }) {
  const adornmentBounds = getAdornmentBounds(nodeX, nodeY, nodeWidth, nodeHeight, orientation);
  const plusIconPosition = getPlusIconPosition(orientation);
  const stemEndPositions = getStemEnds(orientation);

  // eslint-disable-next-line no-unused-vars
  const handleClick = (e) => {
    if (!disabled) {
      onClick();
    }
  };

  useEffect(() => {
    Tooltip.rebuild();
  }, []);

  const keyHandler = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      handleClick(e);
    }
  };

  return (
    <svg
      data-for={disabled ? TooltipConstants.ids.GlobalHover : ''}
      data-tip="We are sorry, but it is not possible to add synthesis steps to molecules without structure."
      data-class="els-tooltip hover-tooltip hover-tooltip--nmgl substance-leaf-node-adornment-tooltip"
      className={cn(
        'rt-project-route-substance-leaf-node-adornment',
        disabled && 'rt-project-route-substance-leaf-node-adornment--disabled'
      )}
      onClick={handleClick}
      onKeyPress={keyHandler}
      role="presentation"
      {...adornmentBounds}>
      <SvgUseAsButton
        {...plusIconPosition}
        width={PLUS_SIZE}
        height={PLUS_SIZE}
        sprite="AddCircle"
        useButton
        tooltip="Add reaction step"
        ariaLabel="add reaction step"
      />
      <line className="rt-project-route-substance-leaf-node-adornment__stem" strokeWidth={PLUS_STEM_HEIGHT} {...stemEndPositions} />
    </svg>
  );
}

SubstanceLeafNodeAdornment.propTypes = {
  nodeX: PropTypes.number,
  nodeY: PropTypes.number,
  nodeWidth: PropTypes.number,
  nodeHeight: PropTypes.number,
  orientation: PropTypes.oneOf(ORIENTATIONS),
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
};

SubstanceLeafNodeAdornment.defaultProps = {
  onClick: () => null,
  disabled: false,
};

export default SubstanceLeafNodeAdornment;
